import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLazyGetProjectUuidQuery } from '../../../../../services/projectService';
import { RootState } from '../../../../../store/store';

interface ProtectedProjectRouteProps {
  children: React.ReactNode;
  requiredStage?: number;
}

const ProtectedProjectRoute: React.FC<ProtectedProjectRouteProps> = ({ 
  children, 
  requiredStage 
}) => {
  const { projectUuid } = useParams<{ projectUuid: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [currentStage, setCurrentStage] = useState<number | null>(null);
  const [triggerGetProject, { data: projectData }] = useLazyGetProjectUuidQuery();
  
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  
  // Проверка авторизации и регистрации
  let isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  let isRegistered = userInfo?.is_registered;
  
  if (isRegistered === undefined) {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        isRegistered = parsedUserInfo.is_registered;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
        isRegistered = false;
      }
    }
  }

  useEffect(() => {
    const fetchProjectData = async () => {
      if (projectUuid) {
        try {
          setIsLoading(true);
          const result: any = await triggerGetProject({ uuid: projectUuid }).unwrap();
          setCurrentStage(result.current_stage);
        } catch (error) {
          console.error('Ошибка загрузки данных проекта:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (projectUuid && isAuthenticated && isRegistered) {
      fetchProjectData();
    } else {
      setIsLoading(false);
    }
  }, [projectUuid, triggerGetProject, isAuthenticated, isRegistered]);

  // Если не авторизован - на логин
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Если не зарегистрирован - на страницу регистрации
  if (!isRegistered) {
    return <Navigate to="/unregistered" replace />;
  }

  // Пока загружаем данные проекта - показываем заглушку
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Загрузка информации о проекте...</div>
    </div>;
  }

  // Если не удалось загрузить проект - возможно, проект не существует
  if (currentStage === null && projectUuid) {
    return <Navigate to="/project" replace />;
  }

  // ЕСЛИ УКАЗАНА ТРЕБУЕМАЯ СТАДИЯ, ПРОВЕРЯЕМ ДОСТУП
  if (requiredStage !== undefined && currentStage !== null) {
    // Если запрашиваемая стадия больше максимально возможной (5) - перенаправляем на главную проекта
    if (requiredStage > 5) {
      return <Navigate to={`/project/${projectUuid}`} replace />;
    }
    
    // Для мониторинга (стадии 2-5) - разрешаем доступ, даже если текущая стадия меньше
    // Пользователь может просматривать мониторинг, но не может выполнять действия на незавершенных этапах
    if (requiredStage >= 2 && requiredStage <= 5) {
      // Разрешаем доступ к просмотру мониторинга
      console.log(`Доступ к мониторингу стадии ${requiredStage} разрешен. Текущая стадия проекта: ${currentStage}`);
      return <>{children}</>;
    }
    
    // Для других случаев (если потребуется)
    // Если запрашиваемая стадия больше текущей - перенаправляем на текущую
    if (requiredStage > currentStage) {
      // Определяем базовый URL в зависимости от текущей стадии
      let redirectUrl = `/project/${projectUuid}`;
      
      // Для стадий 2-5 добавляем /monitoring/N
      if (currentStage >= 2 && currentStage <= 5) {
        redirectUrl = `/project/${projectUuid}/monitoring/${currentStage}`;
      } else if (currentStage === 1) {
        // Для стадии 1 - просто проект (без мониторинга)
        redirectUrl = `/project/${projectUuid}`;
      }
      
      console.log(`Перенаправление: запрошена стадия ${requiredStage}, доступна стадия ${currentStage}`);
      return <Navigate to={redirectUrl} replace />;
    }
  }

  // Все проверки пройдены - отображаем дочерний компонент
  return <>{children}</>;
};

export default ProtectedProjectRoute;